import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PolicyCard } from './policy-card'
import type { PolicyCardData } from './policy-card'
import { AttributeCard } from './attribute-card'
import type { AttributeCardData } from './attribute-card'
import { SubjectListCard } from './subject-list-card'
import type { SubjectListCardData } from './subject-list-card'
import { SubjectDetailCard } from './subject-detail-card'
import type { SubjectDetailData } from './subject-detail-card'
import { SubjectAttributesCard } from './subject-attributes-card'
import type { SubjectAttributesCardData } from './subject-attributes-card'
import { ConnectionListCard } from './connection-list-card'
import type { ConnectionListCardData } from './connection-list-card'
import { ScanResultsCard } from './scan-results-card'
import type { ScanResultsCardData } from './scan-results-card'
import { AuditLogCard } from './audit-log-card'
import type { AuditLogCardData } from './audit-log-card'
import { DecisionLogCard } from './decision-log-card'
import type { DecisionLogCardData } from './decision-log-card'
import { DecisionStatsCard } from './decision-stats-card'
import type { DecisionStatsData } from './decision-stats-card'
import { InfrastructureStatusCard } from './infrastructure-status-card'
import type { InfrastructureStatusCardData } from './infrastructure-status-card'

export interface CardOutput {
  readonly __cardType: string
  readonly [key: string]: unknown
}

const MAX_VISIBLE_CARDS = 3

function hasField(obj: unknown, field: string): boolean {
  return obj !== null && typeof obj === 'object' && field in obj
}

function renderStatusBanner(message: string, variant: 'success' | 'warning' | 'error') {
  const styles = {
    success: 'border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-200',
    warning: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200',
    error: 'border-destructive/30 bg-destructive/10 text-destructive',
  }
  return (
    <div className={`my-1 rounded-md border px-3 py-2 text-sm ${styles[variant]}`}>
      {message}
    </div>
  )
}

function renderPolicyCard(card: CardOutput, onSendMessage?: (text: string) => void) {
  if (!hasField(card, 'card')) return null
  const policyCard = card.card as PolicyCardData
  if (card.__cardType === 'policy-preview') {
    return (
      <PolicyCard
        data={{ ...policyCard, status: 'DRAFT' }}
        isPreview
        onApprove={() => onSendMessage?.('Yes, save this policy as a draft.')}
      />
    )
  }
  return <PolicyCard data={policyCard} />
}

function renderPolicyStatusChange(card: CardOutput) {
  const newStatus = card.newStatus as string
  const message = card.message as string
  return renderStatusBanner(message, newStatus === 'ACTIVE' ? 'success' : 'warning')
}

export function CardRenderer({ card, onSendMessage }: { card: CardOutput; onSendMessage?: (text: string) => void }) {
  switch (card.__cardType) {
    case 'policy':
    case 'policy-preview':
      return renderPolicyCard(card, onSendMessage)

    case 'policy-detail':
      return hasField(card, 'policy') ? <PolicyCard data={card.policy as PolicyCardData} /> : null

    case 'policy-list':
      return <CollapsibleCardList items={(card.policies as PolicyCardData[]) ?? []} type="policy" />

    case 'policy-status-change':
      return renderPolicyStatusChange(card)

    case 'attribute-list':
      return <CollapsibleCardList items={(card.attributes as AttributeCardData[]) ?? []} type="attribute" />

    case 'attribute':
      return hasField(card, 'attribute') ? <AttributeCard data={card.attribute as AttributeCardData} /> : null

    case 'attribute-blocked':
      return hasField(card, 'attribute') ? <AttributeCard data={{ ...(card.attribute as AttributeCardData), isProposed: true }} /> : null

    case 'subject-list':
      return hasField(card, 'subjects') ? <SubjectListCard data={card as unknown as SubjectListCardData} /> : null

    case 'subject-detail':
      return hasField(card, 'subject') ? <SubjectDetailCard data={card as unknown as { message: string; subject: SubjectDetailData }} /> : null

    case 'subject-attributes':
      return hasField(card, 'attributes') ? <SubjectAttributesCard data={card as unknown as SubjectAttributesCardData} /> : null

    case 'connection-list':
      return hasField(card, 'connections') ? <ConnectionListCard data={card as unknown as ConnectionListCardData} /> : null

    case 'connection-detail':
      if (!hasField(card, 'connection')) return null
      return <ConnectionListCard data={{
        message: card.message as string,
        connections: [(card as unknown as { connection: ConnectionListCardData['connections'][number] }).connection],
      }} />

    case 'scan-results':
      return hasField(card, 'results') ? <ScanResultsCard data={card as unknown as ScanResultsCardData} /> : null

    case 'scan-started':
      return renderStatusBanner(card.message as string, 'success')

    case 'scan-preview':
    case 'attribute-update-blocked':
      return renderStatusBanner(card.message as string, 'warning')

    case 'audit-log-list':
      return hasField(card, 'entries') ? <AuditLogCard data={card as unknown as AuditLogCardData} /> : null

    case 'decision-log-list':
      return hasField(card, 'entries') ? <DecisionLogCard data={card as unknown as DecisionLogCardData} /> : null

    case 'decision-stats':
      return hasField(card, 'stats') ? <DecisionStatsCard data={card as unknown as DecisionStatsData} /> : null

    case 'infrastructure-status':
      return hasField(card, 'nodes') ? <InfrastructureStatusCard data={card as unknown as InfrastructureStatusCardData} /> : null

    case 'application-list':
      return <ConnectionListCard data={{
        message: card.message as string,
        connections: (card.applications as ConnectionListCardData['connections']) ?? [],
      }} />

    case 'error':
      return renderStatusBanner(card.message as string, 'error')

    default:
      return null
  }
}

function CollapsibleCardList({ items, type }: { items: (AttributeCardData | PolicyCardData)[]; type: 'attribute' | 'policy' }) {
  const [expanded, setExpanded] = useState(false)
  if (items.length === 0) return null
  const needsCollapse = items.length > MAX_VISIBLE_CARDS
  const visible = needsCollapse && !expanded ? items.slice(0, MAX_VISIBLE_CARDS) : items
  const hiddenCount = items.length - MAX_VISIBLE_CARDS

  return (
    <div className="space-y-1">
      {visible.map((item, i) => {
        const key = ('id' in item && item.id) ? String(item.id) : `${('key' in item ? item.key : (item as PolicyCardData).name)}-${i}`
        if (type === 'attribute') {
          return <AttributeCard key={key} data={item as AttributeCardData} />
        }
        return <PolicyCard key={key} data={item as PolicyCardData} />
      })}
      {needsCollapse && !expanded && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {hiddenCount} more {type === 'attribute' ? 'attributes' : 'policies'}
        </Button>
      )}
    </div>
  )
}
